import React from "react";
import { ModalsKeys } from "global";
import { Stack } from "@mui/material";
import { logError } from "utils/logger";
import { useModal } from "hooks/useModal";
import { FormProvider } from "react-hook-form";
import { QuoteArrayFields, useQuote } from "modules/quote";
import { RenderWithFallback } from "components/shared/Conditional/RenderWithFallback";
import { FormActions } from "./FormActions";
import { FormSections } from "./FormSections";
import { FormInitialLoading } from "./FormInitialLoading";
import { useEquipmentForm } from "../../hooks/useEquipmentForm";
import { useEquipmentActions } from "../../hooks/useEquipmentActions";
import { useEquipmentQuoteForm } from "../../hooks/useEquipmentQuoteForm";
import { prepareCreateUpdateEquipmentDTO, mapQuoteWithUpdatedEquipmentData } from "../../lib";

interface Subscriptions {
  onCreated?: (id: string) => boolean;
  onUpdated?: (id: string) => boolean;
  onDeleted?: () => boolean;
}

interface Props extends Subscriptions {
  VOId?: string;
  dynamicVOId?: string;
  onCancel: VoidFunction;
  equipmentId: string | undefined;
}

export const EquipmentForm = ({ VOId, equipmentId, onCancel, dynamicVOId, ...subscriptions }: Props) => {
  const { handleOpen: handleOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const { onCreateEquipment, onUpdateEquipment, onDeleteEquipment, isAnyActionInProgress } = useEquipmentActions();
  const { form: equipmentForm, formMode, isLoading } = useEquipmentForm({ VOId, id: equipmentId });

  const { handleCreateOrUpdateQuote } = useQuote();
  const { form: equipmentQuoteForm, initialQuote } = useEquipmentQuoteForm({ VOId, id: equipmentId });

  const handleDeleteEquipment = () => {
    const deleteFn = async () => {
      try {
        const isAllIdsExist = equipmentId && dynamicVOId && VOId;

        if (!isAllIdsExist) return;

        await onDeleteEquipment({ equipmentId, dynamicsVendorOpportunityId: dynamicVOId, vendorOpportunityId: VOId });

        subscriptions?.onDeleted?.();
      } catch (error) {
        logError(error);
      }
    };

    handleOpenConfirmationModal({
      message: "Are you sure you want to delete this equipment?",
      onConfirm: deleteFn,
    });
  };

  const handleCreateEquipment = async () => {
    try {
      if (!dynamicVOId || !VOId) return;

      const isEquipmentFormValid = await equipmentForm.trigger();

      if (isEquipmentFormValid) {
        const equipmentFormData = equipmentForm.getValues();

        const equipmentDTO = prepareCreateUpdateEquipmentDTO(equipmentFormData, VOId, dynamicVOId);

        await onCreateEquipment(equipmentDTO).then(id => subscriptions?.onCreated?.(id));
      }
    } catch (error) {
      logError(error);
    }
  };

  const handleUpdateEquipment = async (id: string) => {
    try {
      if (!dynamicVOId || !VOId || !initialQuote) return;

      const validationResults = await Promise.all([equipmentForm.trigger(), equipmentQuoteForm.trigger()]);

      if (validationResults.every(Boolean)) {
        const equipmentFormData = equipmentForm.getValues();
        const equipmentQuoteFormData = equipmentQuoteForm.getValues();

        const mergedQuote = mapQuoteWithUpdatedEquipmentData(initialQuote, equipmentQuoteFormData);
        await handleCreateOrUpdateQuote(VOId, mergedQuote);

        const equipmentDTO = prepareCreateUpdateEquipmentDTO(equipmentFormData, VOId, dynamicVOId);

        await onUpdateEquipment({ ...equipmentDTO, equipmentId: id });
      }
    } catch (error) {
      logError(error);
    }
  };

  const handleSubmitEquipment = async () => {
    if (equipmentId) await handleUpdateEquipment(equipmentId);
    else await handleCreateEquipment();
  };

  return (
    <RenderWithFallback isLoading={isLoading} fallBack={<FormInitialLoading />}>
      <Stack gap="1rem">
        <FormProvider {...equipmentForm}>
          <FormSections />
        </FormProvider>

        {equipmentId && (
          <FormProvider {...equipmentQuoteForm}>
            <QuoteArrayFields equipmentId={equipmentId} />
          </FormProvider>
        )}

        <FormActions
          mode={formMode}
          onCancel={onCancel}
          disabled={isAnyActionInProgress}
          onSubmit={handleSubmitEquipment}
          isLoading={isAnyActionInProgress}
          onDeleteEquipment={handleDeleteEquipment}
        />
      </Stack>
    </RenderWithFallback>
  );
};
